<script setup lang="ts">
import { ref } from 'vue'
import type { TableOptions } from '@core/stores/user'
import type { SortItem } from '@core/types'
import {App} from "@/types/generated";
import UserData = App.Http.Controllers.Auth.UserData;
import { getDateLocale } from '@core/utils/locale'

const { t } = useI18n()
const table = 'audit-log'

definePage({
  meta: {
    action: 'manage',
    subject: 'all',
  },
})

const headers = [
  { title: 'organization', key: 'organization_name', sortable: false },
  { title: 'id', key: 'id', sortable: false },
  { title: 'user_id', key: 'user_id', sortable: false },
  { title: 'created_at', key: 'created_at', sortable: false },
  { title: 'auditable_id', key: 'auditable_id', sortable: false },
  { title: 'auditable_type', key: 'auditable_type', sortable: false },
  { title: 'old_values', key: 'old_values', sortable: false },
  { title: 'new_values', key: 'new_values', sortable: false },
  { title: 'url', key: 'url', sortable: false },
  { title: 'ip_address', key: 'ip_address', sortable: false },
  { title: 'user_agent', key: 'user_agent', sortable: false },
]

const defaultTableOptions: TableOptions = {
  sortBy: 'id',
  orderBy: 'desc',
  page: 1,
  perPage: 10,
}

const cookieTableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

let currentTableOptions: TableOptions | undefined = cookieTableOptions.value

if (currentTableOptions === undefined) {
  currentTableOptions = defaultTableOptions

  const newTableOptions: any = cookieTableOptions.value

  newTableOptions.push(currentTableOptions)

  useCookie(`${table}TableOptions`).value = newTableOptions
}
const userData = useCookie<UserData>('userData')

const userTimeZone = computed(
  () => userData.value?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone ?? 'UTC',
)
const { locale } = useI18n({ useScope: 'global' })
const dateLocale = getDateLocale(locale.value)
const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const itemsPerPage = ref(currentTableOptions.perPage)
const page = ref(currentTableOptions.page)
const sortBy = ref<string>(currentTableOptions.sortBy)
const orderBy = ref<undefined | boolean | 'asc' | 'desc'>(currentTableOptions.orderBy)
const isLoading = ref(false)

const updateOptions = (options: any) => {
  page.value = options.page
  if (options.sortBy[0] !== undefined) {
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
  }
  else {
    sortBy.value = defaultTableOptions.sortBy
    orderBy.value = defaultTableOptions.orderBy
  }
}

const sortTable: SortItem[] = [{ key: sortBy.value, order: orderBy.value }]

const { data: itemsData } = await useApi<any>(createUrl(`/${table}/`,
  {
    query: {
      search: debouncedSearch,
      page,
      itemsPerPage,
      sortBy,
      orderBy,
    },
  },
))

const items = computed(() => itemsData.value.data)

const total = computed(() => itemsData.value.total)

watch([page, itemsPerPage, sortBy, orderBy], (val, old) => {
  if (val[1] !== old[1])
    page.value = 1

  useCookie(`${table}TableOptions`).value = JSON.stringify({
    page: val[0],
    perPage: val[1],
    sortBy: val[2] === undefined ? currentTableOptions?.sortBy : val[2],
    orderBy: val[3] === undefined ? currentTableOptions?.orderBy : val[3],
  })
})
</script>

<template>
  <div>
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>

      </VCardText>
      <div class="category-table">
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :sort-by="sortTable"
          :loading="isLoading"
          :loading-text="t('loading')"
          :items="items"
          :items-length="total"
          class="text-no-wrap"
          @update:options="updateOptions"
        >
          <template #item.created_at="{ item }">
            {{ formatDateToMonthShort(item.created_at, true, dateLocale, userTimeZone) }}
          </template>
          <template #item.old_values="{ item }">
            <div class="d-flex flex-column">
              <span class="text-xs text-no-wrap" style="max-width: 200px; white-space: normal !important;">{{ item.old_values }}</span>
            </div>
          </template>
          <template #item.new_values="{ item }">
            <div class="d-flex flex-column">
              <span class="text-xs text-no-wrap" style="max-width: 200px; white-space: normal !important;">{{ item.new_values }}</span>
            </div>
          </template>
          <template #bottom>
            <div>
              <VDivider/>

              <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 px-6 py-3">
                <AppSelect
                  v-model="itemsPerPage"
                  density="compact"
                  class="app-select flex-grow-0"
                  :items="[5, 10, 25, 50]"
                  style="max-inline-size: 5rem;min-inline-size: 5rem;"
                />

                <VPagination
                  v-model="page"
                  active-color="primary"
                  :length="Math.ceil(total / itemsPerPage)"
                  :total-visible="$vuetify.display.xs ? 1 : Math.min(Math.ceil(total / itemsPerPage), 5)"
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>


  </div>
</template>
